import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import QRCodeStyling from 'qr-code-styling';
import { useNavigate } from 'react-router-dom';
import {
  Download, Save, Loader2, Copy, Check, Link2, MousePointerClick, ChevronDown,
  Grid3x3, Palette, Image as ImageIcon, Wrench,
  Upload, X, Printer, Navigation,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../../components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { toast } from '../../../hooks/use-toast';
import {
  QR_TYPES as TYPE_META, defaultContentFor, buildQrData, generateShortCode, shortLinkUrl, takePendingQrDraft,
  DOT_STYLES, CORNER_SQUARE_STYLES, CORNER_DOT_STYLES, ERROR_CORRECTION_LEVELS,
  GRADIENT_STYLES, CRYPTO_COINS, PAYPAL_TYPES, CURRENCIES,
  DEFAULT_QR_SETTINGS as DEFAULT_SETTINGS, DOWNLOAD_FORMATS, buildQrStyleOptions as buildStyleOptions,
} from '../../../lib/qrEncode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pillClass = (active) =>
  `px-3 py-2 rounded-lg border text-sm font-medium transition-colors text-center ${
    active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
  }`;

const ColorInput = ({ label, value, onChange }) => (
  <div className="space-y-2">
    {label && <Label>{label}</Label>}
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded border flex-shrink-0" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

const AccordionSection = ({ title, icon: Icon, open, onToggle, children }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 hover:bg-gray-200 transition-colors text-left"
    >
      <span className="flex items-center gap-2 font-medium text-gray-800 text-sm">
        <Icon className="w-4 h-4" /> {title}
      </span>
      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div className="p-4 space-y-4 border-t border-gray-200">{children}</div>}
  </div>
);

const QrCodeCreate = ({ editId, basePath = '/admin/qr-builder' }) => {
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const printRef = useRef(null);
  const qrInstanceRef = useRef(null);

  const [name, setName] = useState('');
  const [type, setType] = useState('url');
  const [content, setContent] = useState(defaultContentFor('url'));
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [projectId, setProjectId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isDynamic, setIsDynamic] = useState(false);
  const [shortCode, setShortCode] = useState(null);
  const [clicks, setClicks] = useState(0);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(Boolean(editId));
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [openSections, setOpenSections] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/qr-projects`);
        setProjects(res.data);
      } catch (error) {
        // Non-fatal: the form still works without a project assigned
      }
    })();
  }, []);

  // Pick up whatever someone started on the public, no-login generator
  // before signing in to save it - only for a fresh create, never
  // overriding an existing saved code being edited.
  useEffect(() => {
    if (editId) return;
    const draft = takePendingQrDraft();
    if (draft) {
      setType(draft.type || 'url');
      setContent(draft.content || defaultContentFor(draft.type || 'url'));
      if (draft.settings) setSettings({ ...DEFAULT_SETTINGS, ...draft.settings });
      toast({ title: 'Welcome back', description: 'Picked up where you left off.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Load existing record when editing
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/qr-codes/${editId}`);
        setName(res.data.name);
        setType(res.data.type);
        setContent(res.data.content || defaultContentFor(res.data.type));
        setSettings({ ...DEFAULT_SETTINGS, ...(res.data.settings || {}) });
        setProjectId(res.data.project_id || null);
        setIsDynamic(Boolean(res.data.is_dynamic));
        setShortCode(res.data.short_code || null);
        setClicks(res.data.clicks || 0);
      } catch (error) {
        toast({ title: 'Error', description: 'Could not load this QR code.', variant: 'destructive' });
        navigate(`${basePath}/codes`);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const toggleSection = (id) => {
    setOpenSections((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleToggleDynamic = (checked) => {
    setIsDynamic(checked);
    if (checked && !shortCode) {
      setShortCode(generateShortCode());
    }
  };

  const handleCopyLink = () => {
    if (!shortCode) return;
    navigator.clipboard.writeText(shortLinkUrl(shortCode));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const data = (isDynamic && shortCode) ? shortLinkUrl(shortCode) : (buildQrData(type, content) || ' ');
  const currentTypeLabel = TYPE_META.find((t) => t.value === type)?.label || 'QR';

  // Create the QR instance once, then update it on every change (cheaper than re-mounting).
  // Debounced: qr-code-styling's update() isn't safe to call back-to-back on every
  // keystroke - overlapping calls race internally and can leave it with a half-applied
  // options object (observed as the preview going blank, no thrown error on our side).
  // Coalescing rapid changes into one update 150ms after the last keystroke is
  // imperceptible while typing normally and removes the overlap entirely.
  useEffect(() => {
    if (!previewRef.current) return;
    const timeoutId = setTimeout(() => {
      const options = buildStyleOptions(data, settings);
      if (!qrInstanceRef.current) {
        qrInstanceRef.current = new QRCodeStyling({ width: settings.size, height: settings.size, ...options });
        qrInstanceRef.current.append(previewRef.current);
      } else {
        qrInstanceRef.current.update({ width: settings.size, height: settings.size, ...options });
      }
      // Keep the on-screen preview a fixed, readable size regardless of the
      // configured export resolution - the canvas/svg's real pixels (used for
      // download/print) stay at settings.size.
      const rendered = previewRef.current.querySelector('canvas, svg');
      if (rendered) {
        rendered.style.width = '260px';
        rendered.style.height = '260px';
      }
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [data, settings]);

  const updateContent = useCallback((patch) => {
    setContent((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleTypeChange = (newType) => {
    setType(newType);
    setContent(defaultContentFor(newType));
  };

  const handleDownload = (extension) => {
    if (!qrInstanceRef.current) return;
    qrInstanceRef.current.download({ name: name || 'qr-code', extension });
  };

  const handlePrint = () => {
    const rendered = previewRef.current?.querySelector('canvas, svg');
    if (!rendered || !printRef.current) return;
    printRef.current.innerHTML = '';
    const img = document.createElement('img');
    if (rendered.tagName === 'CANVAS') {
      img.src = rendered.toDataURL('image/png');
    } else {
      const svgData = new XMLSerializer().serializeToString(rendered);
      img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
    }
    img.style.width = '320px';
    img.style.height = '320px';
    printRef.current.appendChild(img);
    window.print();
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Error', description: 'Please choose an image file.', variant: 'destructive' });
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'qr-logos');
      const res = await axios.post(`${API}/storage/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSettings((s) => ({ ...s, logoUrl: res.data.url }));
    } catch (error) {
      toast({ title: 'Error', description: 'Could not upload the logo.', variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => updateContent({ latitude: String(pos.coords.latitude), longitude: String(pos.coords.longitude) }),
      () => toast({ title: 'Error', description: 'Could not get your location.', variant: 'destructive' })
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({ title: 'Name required', description: 'Give this QR code a name so you can find it later.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(), type, content, data, settings,
        project_id: projectId || null,
        is_dynamic: isDynamic,
        short_code: isDynamic ? shortCode : null,
      };
      if (editId) {
        await axios.put(`${API}/qr-codes/${editId}`, payload);
        toast({ title: 'Saved', description: 'Your QR code has been updated.' });
      } else {
        await axios.post(`${API}/qr-codes`, payload);
        toast({ title: 'Saved', description: 'Your QR code has been created.' });
      }
      navigate(`${basePath}/codes`);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Could not save this QR code.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto print:hidden">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {editId ? `Edit ${currentTypeLabel} QR Code` : `${currentTypeLabel} QR code generator`}
          </h1>
          <p className="text-gray-500 text-sm">
            {editId ? 'Update the content and style, then save.' : `Generate easy & customizable ${currentTypeLabel} QR codes in minutes.`}
          </p>
        </div>

        {/* Type selector */}
        <TooltipProvider delayDuration={200}>
          <div className="flex flex-wrap gap-2 mb-6">
            {TYPE_META.map((t) => (
              <Tooltip key={t.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleTypeChange(t.value)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      type === t.value ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <t.icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t.tooltip}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Business Card Link" />
                  </div>
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Select value={projectId || '__none'} onValueChange={(v) => setProjectId(v === '__none' ? null : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">No Project</SelectItem>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: p.color }} />
                              {p.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {type === 'url' && (
                  <div className="space-y-2">
                    <Label>URL</Label>
                    <Input value={content.url || ''} onChange={(e) => updateContent({ url: e.target.value })} placeholder="https://example.com" />
                  </div>
                )}

                {type === 'text' && (
                  <div className="space-y-2">
                    <Label>Text content</Label>
                    <Textarea value={content.text || ''} onChange={(e) => updateContent({ text: e.target.value })} rows={4} />
                  </div>
                )}

                {type === 'phone' && (
                  <div className="space-y-2">
                    <Label>Phone number</Label>
                    <Input value={content.phone || ''} onChange={(e) => updateContent({ phone: e.target.value })} placeholder="+1 555 123 4567" />
                  </div>
                )}

                {type === 'sms' && (
                  <>
                    <div className="space-y-2">
                      <Label>Phone number</Label>
                      <Input value={content.phone || ''} onChange={(e) => updateContent({ phone: e.target.value })} placeholder="+1 555 123 4567" />
                    </div>
                    <div className="space-y-2">
                      <Label>Prefilled message</Label>
                      <Textarea value={content.body || ''} onChange={(e) => updateContent({ body: e.target.value })} rows={3} />
                    </div>
                  </>
                )}

                {type === 'email' && (
                  <>
                    <div className="space-y-2">
                      <Label>Email address</Label>
                      <Input value={content.email || ''} onChange={(e) => updateContent({ email: e.target.value })} placeholder="you@example.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Prefilled subject</Label>
                      <Input value={content.subject || ''} onChange={(e) => updateContent({ subject: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Prefilled message</Label>
                      <Textarea value={content.body || ''} onChange={(e) => updateContent({ body: e.target.value })} rows={3} />
                    </div>
                  </>
                )}

                {type === 'whatsapp' && (
                  <>
                    <div className="space-y-2">
                      <Label>Phone number</Label>
                      <Input value={content.phone || ''} onChange={(e) => updateContent({ phone: e.target.value })} placeholder="+1 555 123 4567" />
                    </div>
                    <div className="space-y-2">
                      <Label>Prefilled message</Label>
                      <Textarea value={content.body || ''} onChange={(e) => updateContent({ body: e.target.value })} rows={3} />
                    </div>
                  </>
                )}

                {type === 'facetime' && (
                  <div className="space-y-2">
                    <Label>Facetime (phone or email)</Label>
                    <Input value={content.facetime || ''} onChange={(e) => updateContent({ facetime: e.target.value })} placeholder="+1 555 123 4567 or you@example.com" />
                  </div>
                )}

                {type === 'location' && (
                  <>
                    <div className="flex items-center justify-between">
                      <Label>Coordinates</Label>
                      <Button type="button" size="sm" variant="outline" onClick={handleUseMyLocation}>
                        <Navigation className="w-3.5 h-3.5 mr-1.5" /> Use my location
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input value={content.latitude || ''} onChange={(e) => updateContent({ latitude: e.target.value })} placeholder="Latitude" />
                      <Input value={content.longitude || ''} onChange={(e) => updateContent({ longitude: e.target.value })} placeholder="Longitude" />
                    </div>
                  </>
                )}

                {type === 'wifi' && (
                  <>
                    <div className="space-y-2">
                      <Label>Network name (SSID)</Label>
                      <Input value={content.ssid || ''} onChange={(e) => updateContent({ ssid: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Security</Label>
                      <Select value={content.encryption || 'WPA'} onValueChange={(v) => updateContent({ encryption: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="WPA">WPA/WPA2</SelectItem>
                          <SelectItem value="WEP">WEP</SelectItem>
                          <SelectItem value="nopass">None</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {content.encryption !== 'nopass' && (
                      <div className="space-y-2">
                        <Label>Password</Label>
                        <Input value={content.password || ''} onChange={(e) => updateContent({ password: e.target.value })} />
                      </div>
                    )}
                  </>
                )}

                {type === 'event' && (
                  <>
                    <div className="space-y-2">
                      <Label>Event name</Label>
                      <Input value={content.event || ''} onChange={(e) => updateContent({ event: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Starts</Label>
                        <Input type="datetime-local" value={content.event_start_datetime || ''} onChange={(e) => updateContent({ event_start_datetime: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Ends</Label>
                        <Input type="datetime-local" value={content.event_end_datetime || ''} onChange={(e) => updateContent({ event_end_datetime: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input value={content.event_location || ''} onChange={(e) => updateContent({ event_location: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>URL</Label>
                      <Input value={content.event_url || ''} onChange={(e) => updateContent({ event_url: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Note</Label>
                      <Textarea value={content.event_note || ''} onChange={(e) => updateContent({ event_note: e.target.value })} rows={2} />
                    </div>
                  </>
                )}

                {type === 'crypto' && (
                  <>
                    <div className="space-y-2">
                      <Label>Coin</Label>
                      <Select value={content.crypto_coin} onValueChange={(v) => updateContent({ crypto_coin: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CRYPTO_COINS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Wallet address</Label>
                      <Input value={content.crypto_address || ''} onChange={(e) => updateContent({ crypto_address: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Amount (optional)</Label>
                      <Input value={content.crypto_amount || ''} onChange={(e) => updateContent({ crypto_amount: e.target.value })} placeholder="0.00" />
                    </div>
                  </>
                )}

                {type === 'vcard' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First name</Label>
                        <Input value={content.first_name || ''} onChange={(e) => updateContent({ first_name: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Last name</Label>
                        <Input value={content.last_name || ''} onChange={(e) => updateContent({ last_name: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input value={content.email || ''} onChange={(e) => updateContent({ email: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Website URL</Label>
                      <Input value={content.website || ''} onChange={(e) => updateContent({ website: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Company</Label>
                      <Input value={content.company || ''} onChange={(e) => updateContent({ company: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Job title</Label>
                      <Input value={content.job_title || ''} onChange={(e) => updateContent({ job_title: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Birthday</Label>
                      <Input type="date" value={content.birthday || ''} onChange={(e) => updateContent({ birthday: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Street address</Label>
                      <Input value={content.street || ''} onChange={(e) => updateContent({ street: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input value={content.city || ''} onChange={(e) => updateContent({ city: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP</Label>
                        <Input value={content.zip || ''} onChange={(e) => updateContent({ zip: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Region</Label>
                        <Input value={content.region || ''} onChange={(e) => updateContent({ region: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Input value={content.country || ''} onChange={(e) => updateContent({ country: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Note</Label>
                      <Textarea value={content.note || ''} onChange={(e) => updateContent({ note: e.target.value })} rows={2} />
                    </div>
                  </>
                )}

                {type === 'paypal' && (
                  <>
                    <div className="space-y-2">
                      <Label>Button Type</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {PAYPAL_TYPES.map((pt) => (
                          <button key={pt.value} type="button" onClick={() => updateContent({ paypal_type: pt.value })} className={pillClass(content.paypal_type === pt.value)}>
                            {pt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Business Email</Label>
                      <Input value={content.paypal_email || ''} onChange={(e) => updateContent({ paypal_email: e.target.value })} placeholder="you@example.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Item Name</Label>
                      <Input value={content.paypal_title || ''} onChange={(e) => updateContent({ paypal_title: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Price</Label>
                        <Input value={content.paypal_price || ''} onChange={(e) => updateContent({ paypal_price: e.target.value })} placeholder="0.00" />
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select value={content.paypal_currency} onValueChange={(v) => updateContent({ paypal_currency: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Thank You URL</Label>
                        <Input value={content.paypal_thank_you_url || ''} onChange={(e) => updateContent({ paypal_thank_you_url: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Cancel URL</Label>
                        <Input value={content.paypal_cancel_url || ''} onChange={(e) => updateContent({ paypal_cancel_url: e.target.value })} />
                      </div>
                    </div>
                  </>
                )}

                {/* Dynamic QR */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="pr-4">
                      <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                        <Link2 className="w-3.5 h-3.5 text-[#FF6B00]" /> Dynamic QR code
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Enabling this will create a QR code that points to an internal short link,
                        which redirects to the content above &mdash; edit it anytime without reprinting.
                      </p>
                    </div>
                    <Switch checked={isDynamic} onCheckedChange={handleToggleDynamic} />
                  </div>

                  {isDynamic && shortCode && (
                    <div className="flex items-center justify-between gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 mt-3">
                      <code className="text-sm text-gray-800 truncate">{shortLinkUrl(shortCode)}</code>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {editId && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <MousePointerClick className="w-3.5 h-3.5" /> {clicks} scan{clicks === 1 ? '' : 's'}
                          </span>
                        )}
                        <Button type="button" size="sm" variant="outline" onClick={handleCopyLink}>
                          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Accordions */}
                <div className="space-y-3 pt-2">
                  <AccordionSection title="Style" icon={Grid3x3} open={openSections.includes('style')} onToggle={() => toggleSection('style')}>
                    <div className="space-y-2">
                      <Label>Dot Style</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {DOT_STYLES.map((o) => (
                          <button key={o.value} type="button" onClick={() => setSettings((s) => ({ ...s, dotStyle: o.value }))} className={pillClass(settings.dotStyle === o.value)}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Inner Eye Style</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {CORNER_DOT_STYLES.map((o) => (
                          <button key={o.value} type="button" onClick={() => setSettings((s) => ({ ...s, cornerDotStyle: o.value }))} className={pillClass(settings.cornerDotStyle === o.value)}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Outer Eye Style</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {CORNER_SQUARE_STYLES.map((o) => (
                          <button key={o.value} type="button" onClick={() => setSettings((s) => ({ ...s, cornerSquareStyle: o.value }))} className={pillClass(settings.cornerSquareStyle === o.value)}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </AccordionSection>

                  <AccordionSection title="Colors" icon={Palette} open={openSections.includes('colors')} onToggle={() => toggleSection('colors')}>
                    <div className="space-y-2">
                      <Label>Foreground Type</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => setSettings((s) => ({ ...s, foregroundType: 'color' }))} className={pillClass(settings.foregroundType === 'color')}>Color</button>
                        <button type="button" onClick={() => setSettings((s) => ({ ...s, foregroundType: 'gradient' }))} className={pillClass(settings.foregroundType === 'gradient')}>Gradient</button>
                      </div>
                    </div>

                    {settings.foregroundType === 'color' ? (
                      <ColorInput label="Foreground Color" value={settings.fgColor} onChange={(v) => setSettings((s) => ({ ...s, fgColor: v }))} />
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label>Gradient Style</Label>
                          <Select value={settings.gradientStyle} onValueChange={(v) => setSettings((s) => ({ ...s, gradientStyle: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {GRADIENT_STYLES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <ColorInput label="Color 1" value={settings.gradientColor1} onChange={(v) => setSettings((s) => ({ ...s, gradientColor1: v }))} />
                          <ColorInput label="Color 2" value={settings.gradientColor2} onChange={(v) => setSettings((s) => ({ ...s, gradientColor2: v }))} />
                        </div>
                      </>
                    )}

                    <ColorInput label="Background Color" value={settings.bgColor} onChange={(v) => setSettings((s) => ({ ...s, bgColor: v }))} />

                    <div className="space-y-2">
                      <Label>Background Transparency ({settings.bgTransparency}%)</Label>
                      <input
                        type="range" min={0} max={100} value={settings.bgTransparency}
                        onChange={(e) => setSettings((s) => ({ ...s, bgTransparency: Number(e.target.value) }))}
                        className="w-full"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Custom Eyes Color</Label>
                      <Switch checked={settings.customEyesColor} onCheckedChange={(v) => setSettings((s) => ({ ...s, customEyesColor: v }))} />
                    </div>

                    {settings.customEyesColor && (
                      <div className="grid grid-cols-2 gap-4">
                        <ColorInput label="Eyes Inner Color" value={settings.eyesInnerColor} onChange={(v) => setSettings((s) => ({ ...s, eyesInnerColor: v }))} />
                        <ColorInput label="Eyes Outer Color" value={settings.eyesOuterColor} onChange={(v) => setSettings((s) => ({ ...s, eyesOuterColor: v }))} />
                      </div>
                    )}
                  </AccordionSection>

                  <AccordionSection title="Branding" icon={ImageIcon} open={openSections.includes('branding')} onToggle={() => toggleSection('branding')}>
                    <div className="space-y-2">
                      <Label>Logo</Label>
                      <div className="flex items-center gap-3">
                        <input type="file" accept="image/*" id="qr-logo-upload" className="hidden" onChange={handleLogoUpload} />
                        <label htmlFor="qr-logo-upload" className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 text-sm">
                          {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          {settings.logoUrl ? 'Change Logo' : 'Choose File'}
                        </label>
                        {settings.logoUrl && (
                          <>
                            <img src={settings.logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded border" />
                            <Button type="button" size="sm" variant="outline" onClick={() => setSettings((s) => ({ ...s, logoUrl: null }))}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">JPG, PNG, GIF.</p>
                    </div>

                    {settings.logoUrl && (
                      <div className="space-y-2">
                        <Label>Logo Size ({settings.logoSize}%)</Label>
                        <input
                          type="range" min={5} max={35} value={settings.logoSize}
                          onChange={(e) => setSettings((s) => ({ ...s, logoSize: Number(e.target.value) }))}
                          className="w-full"
                        />
                      </div>
                    )}
                  </AccordionSection>

                  <AccordionSection title="Options" icon={Wrench} open={openSections.includes('options')} onToggle={() => toggleSection('options')}>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Size (px)</Label>
                        <Input type="number" min={100} max={2000} value={settings.size} onChange={(e) => setSettings((s) => ({ ...s, size: Number(e.target.value) || 400 }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Margin (px)</Label>
                        <Input type="number" min={0} max={50} value={settings.margin} onChange={(e) => setSettings((s) => ({ ...s, margin: Number(e.target.value) || 0 }))} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Error Correction</Label>
                      <Select value={settings.ecc} onValueChange={(v) => setSettings((s) => ({ ...s, ecc: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ERROR_CORRECTION_LEVELS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </AccordionSection>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Live preview */}
          <div>
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-base">Preview</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-4">
                <div ref={previewRef} className="border rounded-lg p-2 bg-white" data-testid="qr-preview" />
                <div className="grid grid-cols-2 gap-2 w-full">
                  <Button variant="outline" onClick={handlePrint}>
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="bg-gray-900 hover:bg-gray-800">
                        <Download className="w-4 h-4 mr-2" /> Download <ChevronDown className="w-4 h-4 ml-1.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {DOWNLOAD_FORMATS.map((ext) => (
                        <DropdownMenuItem key={ext} onClick={() => handleDownload(ext)}>
                          Download as {ext.toUpperCase()}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <Button className="w-full bg-[#FF6B00] hover:bg-[#e05f00]" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editId ? 'Update QR Code' : 'Save QR Code'}
                </Button>
                <p className="text-xs text-gray-400 text-center">
                  Make sure to test your configured QR code before using it in production.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Print-only: just the QR, nothing else on the page */}
      <div ref={printRef} className="hidden print:flex print:fixed print:inset-0 print:items-center print:justify-center print:bg-white" />
    </>
  );
};

export default QrCodeCreate;
